import { all, call, put, select, takeLatest, takeLeading, throttle } from 'redux-saga/effects';
import fileDownload from 'js-file-download';
import { fetchClient } from '../utilities/fetchClient';
import ActionTypes from '../actions/actionTypes';
import {
  cardsDownloadSuccess,
  cardsDownloadFailure,
  getCardsFailure,
  getCardsSuccess,
  getCardDetailFailure,
  getCardDetailSuccess,
  cancelPendingTransferBatchSuccess,
  cancelPendingTransferBatchFailure,
  updatePendingTransferBatchSuccess,
  updatePendingTransferBatchFailure,
  pendingTransfersDownloadSuccess,
  pendingTransfersDownloadFailure,
  resetCreateCard,
  resetEditCard,
  resetCardOptions,
  transferBatchCardsReset,
  resetActivateCard,
  resetActivateMultipleCards,
  resetSingleCardStatus,
  resetMultipleCardStatus,
  resetUpdateMultipleCardDetails,
  transferSingleCardsReset,
  cardsResetUpdatePin,
  cardsResetPinReset,
  showCardCreatedModal,
} from '../actions/cardActions';
import { cardsDownloadFileName, pendingTransfersDownloadFileName } from '../constants';
import { isMobile } from '../utilities/usersDevice';
import Env from '../Env';
import { addToastAlert } from 'actions/toastAlertActions';
import Alert from 'constants/alert-messages';
import { setAssignedGrandparentAccount } from 'actions/userActions';
import { getCashOrdersListSuccess } from '../actions/cashActions';
import { MEDIUM_THROTTLE } from '../constants';
import SURVEYS from '../constants/surveys';

function* getCardsFile(action) {
  let headerPayload = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };
  try {
    yield put(addToastAlert(Alert.DOWNLOAD_CARDS_LOADING));
    const response = yield call(fetchClient, {
      method: 'GET',
      url: `cards-api/v1/cards/contract/${action.payload.contractNumber}/download`,
      apiServer: Env.getApimBaseUrl(),
      headers: headerPayload,
      queryParams: {
        parent: action.payload.parent,
        ...action.payload.cardNumber,
      },
      responseType: 'blob',
    });

    yield call(fileDownload, response, cardsDownloadFileName);
    yield put(cardsDownloadSuccess());
    yield put(addToastAlert(Alert.DOWNLOAD_CARDS_SUCCESS, 'success'));
  } catch {
    yield put(cardsDownloadFailure());
    yield put(addToastAlert(Alert.DOWNLOAD_CARDS_ERROR, 'error'));
  }
}

/**
 * Get cards based on the account number.
 * */
function* getCards(action) {
  let headerPayload = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };

  const { contractNumber, isFilterClick, ...queryParams } = action.payload;

  try {
    let response = yield call(fetchClient, {
      url: `cards-api/v1/cards/contract/${contractNumber}`,
      method: 'GET',
      apiServer: Env.getApimBaseUrl(),
      headers: headerPayload,
      queryParams: {
        ...queryParams,
      },
    });

    yield put(getCardsSuccess(response));
    if (response.resultMessage && isFilterClick) {
      yield put(addToastAlert(response.resultMessage, 'success'));
    }
  } catch {
    yield put(getCardsFailure());
  }
}

/**
 * Get card details based on card number.
 * */
function* getCardDetail(action) {
  let headerPayload = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };

  try {
    let response = yield call(fetchClient, {
      url: `cards-api/v1/cards/${action.payload.cardNumber}/detail`,
      method: 'GET',
      apiServer: Env.getApimBaseUrl(),
      headers: headerPayload,
      queryParams: {
        grandParentAccountNumber: action.payload.grandParentAccountNumber,
      },
    });

    yield put(getCardDetailSuccess(response));
  } catch {
    yield put(getCardDetailFailure());
    yield put(addToastAlert(Alert.GET_CARD_DETAIL_ERROR, 'error', true));
  }
}

function* updatePinForCards(action) {
  const header = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };

  const { ...queryParams } = action.payload;

  try {
    yield put(addToastAlert(Alert.UPDATE_PIN_LOADING));
    yield call(fetchClient, {
      url: 'cards-api/v1/cards/pin',
      method: 'POST',
      body: queryParams,
      headers: header,
      apiServer: Env.getApimBaseUrl(),
    });
    yield put({
      type: ActionTypes.CARDS.UPDATE_PIN.SUCCESS,
    });

    if (Env.getIsZonkaEnabled() && typeof window._zf === 'function' && SURVEYS.SETTING_OR_RESETTING_PIN) {
      window._zf('startForm', { widgetId: SURVEYS.SETTING_OR_RESETTING_PIN, throttle: 'session' });
    }
    yield put(addToastAlert(Alert.UPDATE_PIN_SUCCESS, 'success'));
    yield put(cardsResetUpdatePin());
  } catch (e) {
    yield put({ type: ActionTypes.CARDS.UPDATE_PIN.FAILURE, payload: e });
    yield put(addToastAlert(Alert.UPDATE_PIN_ERROR, 'error'));
    yield put(cardsResetUpdatePin());
  }
}

function* resetPinForCards(action) {
  const header = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };

  const { ...queryParams } = action.payload;

  try {
    yield put(addToastAlert(Alert.RESET_PIN_LOADING));
    yield call(fetchClient, {
      url: 'cards-api/v1/cards/list/pin/reset',
      method: 'POST',
      body: queryParams,
      headers: header,
      apiServer: Env.getApimBaseUrl(),
    });
    yield put({
      type: ActionTypes.CARDS.RESET_PIN.SUCCESS,
    });

    if (Env.getIsZonkaEnabled() && typeof window._zf === 'function' && SURVEYS.SETTING_OR_RESETTING_PIN) {
      window._zf('startForm', { widgetId: SURVEYS.SETTING_OR_RESETTING_PIN, throttle: 'session' });
    }
    yield put(addToastAlert(Alert.RESET_PIN_SUCCESS, 'success'));

    yield put(cardsResetPinReset());
  } catch (e) {
    yield put({ type: ActionTypes.CARDS.RESET_PIN.FAILURE, payload: e });
    yield put(addToastAlert(Alert.RESET_PIN_ERROR, 'error'));
    yield put(cardsResetPinReset());
  }
}

function* transferSingle(action) {
  const header = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };

  try {
    yield put(addToastAlert(Alert.TRANSFER_CARD_LOADING));
    yield call(fetchClient, {
      url: 'cards-api/v1/cards/transfer',
      method: 'POST',
      body: action.payload,
      headers: header,
      apiServer: Env.getApimBaseUrl(),
    });
    yield put({
      type: ActionTypes.CARDS.TRANSFER_SINGLE.SUCCESS,
    });
    yield put(addToastAlert(Alert.TRANSFER_CARD_SUCCESS, 'success'));
    yield put(transferSingleCardsReset());
  } catch (e) {
    yield put({ type: ActionTypes.CARDS.TRANSFER_SINGLE.FAILURE, payload: e });
    const errorResponse = e?.response?.data?.errorMessage || '';
    const transferErrorMessage = getTransferCardErrorMessage(errorResponse) || Alert.TRANSFER_CARD_ERROR;
    yield put(addToastAlert(transferErrorMessage, 'error'));
    yield put(transferSingleCardsReset());
  }
}

function* transferBatch(action) {
  const header = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };

  try {
    yield put(addToastAlert(Alert.TRANSFER_MULTIPLE_CARD_LOADING));
    yield call(fetchClient, {
      url: 'cards-api/v1/cards/transfer',
      method: 'POST',
      body: action.payload,
      headers: header,
      apiServer: Env.getApimBaseUrl(),
    });
    yield put({
      type: ActionTypes.CARDS.TRANSFER_BATCH.SUCCESS,
    });
    yield put(addToastAlert(Alert.TRANSFER_MULTIPLE_CARD_SUCCESS, 'success'));
    yield put(transferBatchCardsReset());
  } catch (e) {
    yield put({ type: ActionTypes.CARDS.TRANSFER_BATCH.FAILURE, payload: e });
    const errorResponse = e?.response?.data?.errorMessage || '';
    const transferErrorMessage = getTransferCardErrorMessage(errorResponse) || Alert.TRANSFER_MULTIPLE_CARD_ERROR;
    yield put(addToastAlert(transferErrorMessage, 'error'));
    yield put(transferBatchCardsReset());
  }
}

function* activateCard(action) {
  const header = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };

  try {
    yield put(addToastAlert(Alert.ACTIVATE_CARD_LOADING));
    yield call(fetchClient, {
      url: `cards-api/v1/cards/activate/${action.payload.cardNumber}`,
      method: 'POST',
      body: action.payload.activateCardRequest,
      headers: header,
      apiServer: Env.getApimBaseUrl(),
    });
    yield put({
      type: ActionTypes.CARDS.ACTIVATE.SUCCESS,
    });
    yield put(addToastAlert(Alert.ACTIVATE_CARD_SUCCESS, 'success'));
    yield put(resetActivateCard());
  } catch (e) {
    yield put({ type: ActionTypes.CARDS.ACTIVATE.FAILURE, payload: e });
    yield put(addToastAlert(Alert.ACTIVATE_CARD_ERROR, 'error'));
    yield put(resetActivateCard());
  }
}

function* activateMultipleCards(action) {
  const header = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };

  try {
    yield put(addToastAlert(Alert.ACTIVATE_MULTIPLE_LOADING));
    const response = yield call(fetchClient, {
      url: 'cards-api/v1/cards/activate',
      method: 'POST',
      body: action.payload,
      headers: header,
      apiServer: Env.getApimBaseUrl(),
    });
    yield put({
      type: ActionTypes.CARDS.ACTIVATE_MULTIPLE.SUCCESS,
    });
    yield put(addToastAlert(response.status ?? Alert.ACTIVATE_MULTIPLE_SUCCESS, 'success'));
    yield put(resetActivateMultipleCards());
  } catch (e) {
    yield put({ type: ActionTypes.CARDS.ACTIVATE_MULTIPLE.FAILURE, payload: e });
    yield put(addToastAlert(Alert.ACTIVATE_MULTIPLE_ERROR, 'error'));
    yield put(resetActivateMultipleCards());
  }
}

function* setCardStatus(action) {
  const header = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };

  try {
    yield put(addToastAlert(Alert.UPDATE_CARD_STATUS_LOADING));
    yield call(fetchClient, {
      url: 'cards-api/v1/cards/status',
      method: 'POST',
      body: action.payload.setCardStatusRequest,
      headers: header,
      apiServer: Env.getApimBaseUrl(),
    });
    yield put({
      type: ActionTypes.CARDS.STATUS.SUCCESS,
    });
    if (Env.getIsZonkaEnabled() && typeof window._zf === 'function' && SURVEYS.UPDATING_CARD_STATUS) {
      window._zf('startForm', { widgetId: SURVEYS.UPDATING_CARD_STATUS, throttle: 'session' });
    }
    yield put(addToastAlert(Alert.UPDATE_CARD_STATUS_SUCCESS, 'success'));
    yield put(resetSingleCardStatus());
  } catch (e) {
    yield put({ type: ActionTypes.CARDS.STATUS.FAILURE, payload: e });
    yield put(addToastAlert(Alert.UPDATE_CARD_STATUS_ERROR, 'error'));
    yield put(resetSingleCardStatus());
  }
}

function* setMultipleCardStatus(action) {
  const header = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };

  try {
    yield put(addToastAlert(Alert.UPDATE_MULTIPLE_CARD_STATUS_LOADING));
    yield call(fetchClient, {
      url: 'cards-api/v1/cards/list/status',
      method: 'POST',
      body: action.payload.setMultipleCardStatusRequest,
      headers: header,
      apiServer: Env.getApimBaseUrl(),
    });
    yield put({
      type: ActionTypes.CARDS.MULTIPLE_CARDS_STATUS.SUCCESS,
    });
    yield put(addToastAlert(Alert.UPDATE_MULTIPLE_CARD_STATUS_SUCCESS, 'success'));
    yield put(resetMultipleCardStatus());
  } catch (e) {
    const errorResponse = e?.response?.data?.errorMessage || '';
    let errorMessage;
    // Feature flag: When VITE_EV_WAY4 is disabled, show AmpCharge-specific errors
    if (errorResponse === Alert.CUSTOMER_SUPPORT_ERROR) {
      errorMessage = Alert.CUSTOMER_SUPPORT_ERROR;
    } else if (!Env.getIsEvWay4Enabled()) {
      const isAmpcharge = yield* getIsAmpcharge();
      errorMessage =
        isAmpcharge || isAmpchargeErrorMessage(errorResponse)
          ? Alert.UPDATE_MULTIPLE_CARD_STATUS_ERROR_AMPCHARGE
          : Alert.UPDATE_MULTIPLE_CARD_STATUS_ERROR;
    } else {
      errorMessage = Alert.UPDATE_MULTIPLE_CARD_STATUS_ERROR;
    }
    yield put({ type: ActionTypes.CARDS.MULTIPLE_CARDS_STATUS.FAILURE, payload: e });
    yield put(addToastAlert(errorMessage, 'error'));
    yield put(resetMultipleCardStatus());
  }
}

function* setCardUsageOptions(action) {
  const header = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };
  try {
    yield put(addToastAlert(Alert.EDIT_CARD_OPTIONS_LOADING));
    yield call(fetchClient, {
      url: `cards-api/v1/cards/${action.payload.grandfatherClientNumber}/usage`,
      method: 'POST',
      body: action.payload.updateMultipleCardRequest,
      headers: header,
      apiServer: Env.getApimBaseUrl(),
    });
    yield put({
      type: ActionTypes.CARDS.CARD_OPTIONS.SUCCESS,
    });
    yield put(addToastAlert(Alert.EDIT_CARD_OPTIONS_SUCCESS, 'success'));

    if (Env.getIsZonkaEnabled() && typeof window._zf === 'function' && SURVEYS.SUBMITTING_EDIT_CARD_REQUEST) {
      window._zf('startForm', { widgetId: SURVEYS.SUBMITTING_EDIT_CARD_REQUEST, throttle: 'session' });
    }
    yield put(resetCardOptions());
  } catch (e) {
    yield put({ type: ActionTypes.CARDS.CARD_OPTIONS.FAILURE, payload: e });
    // Feature flag: When VITE_EV_WAY4 is disabled, check for AmpCharge-specific errors
    const errorResponse = e?.response?.data?.errorMessage || '';
    const errorMessage =
      !Env.getIsEvWay4Enabled() && isAmpchargeErrorMessage(errorResponse)
        ? Alert.EDIT_CARD_OPTIONS_ERROR_AMPCHARGE
        : Alert.EDIT_CARD_OPTIONS_ERROR;
    yield put(addToastAlert(errorMessage, 'error'));
    yield put(resetCardOptions());
  }
}

function* getPendingTransferCards(action) {
  const header = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };

  try {
    const response = yield call(fetchClient, {
      url: 'cards-api/v1/cards/transfer/pending',
      method: 'GET',
      queryParams: {
        ...action.payload,
      },
      headers: header,
      apiServer: Env.getApimBaseUrl(),
    });

    yield put({
      type: ActionTypes.CARDS.PENDING.SUCCESS,
      payload: {
        pendingCardsList: response.result,
        pendingCardsSize: response.resultSize,
      },
    });
  } catch (e) {
    yield put({ type: ActionTypes.CARDS.PENDING.FAILURE, payload: e });
  }
}

function* getCardAccountDefaults(action) {
  const header = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };

  try {
    const response = yield call(fetchClient, {
      url: `cards-api/v1/cards/contract/${action.payload.contractNumber}/default`,
      method: 'GET',
      queryParams: {
        ...action.payload,
      },
      headers: header,
      apiServer: Env.getApimBaseUrl(),
    });

    yield put({
      type: ActionTypes.CARDS.ACCOUNT_DEFAULT.SUCCESS,
      payload: response,
    });
  } catch (e) {
    yield put({ type: ActionTypes.CARDS.ACCOUNT_DEFAULT.FAILURE, payload: e });
    yield put(addToastAlert(Alert.FETCH_ACC_DEFAULTS_ERROR, 'error'));
  }
}

/**
 * Create Card Orchestration
 * */
function* createCard(action) {
  let headerPayload = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };
  try {
    yield put(addToastAlert(Alert.CREATE_CARD_LOADING));
    const response = yield call(fetchClient, {
      url: 'cards-api/v1/cards',
      method: 'POST',
      body: action.payload.createCardRequest,
      headers: headerPayload,
      apiServer: Env.getApimBaseUrl(),
    });
    yield put({
      type: ActionTypes.CARDS.CREATE.SUCCESS,
      payload: response,
    });
    yield put(showCardCreatedModal(response?.cardNumber));

    if (Env.getIsZonkaEnabled() && typeof window._zf === 'function' && SURVEYS.ORDERING_NEW_CARD) {
      window._zf('startForm', { widgetId: SURVEYS.ORDERING_NEW_CARD, throttle: 'session' });
    }
    yield put(resetCreateCard());
  } catch (e) {
    yield put({
      type: ActionTypes.CARDS.CREATE.FAILURE,
      payload: e,
    });
    yield put(addToastAlert(Alert.CREATE_CARD_ERROR, 'error'));
    yield put(resetCreateCard());
  }
}

function* editCardDetail(action) {
  const header = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };

  try {
    yield put(addToastAlert(Alert.EDIT_CARD_LOADING));
    const response = yield call(fetchClient, {
      url: `cards-api/v1/cards/${action.payload.grandfatherClientNumber}`,
      method: 'POST',
      body: action.payload.updateCardRequest,
      headers: header,
      apiServer: Env.getApimBaseUrl(),
    });
    yield put({
      type: ActionTypes.CARDS.EDIT.SUCCESS,
      payload: {
        details: response,
      },
    });
    yield put(addToastAlert(Alert.EDIT_CARD_SUCCESS, 'success'));

    if (Env.getIsZonkaEnabled() && typeof window._zf === 'function' && SURVEYS.SUBMITTING_EDIT_CARD_REQUEST) {
      window._zf('startForm', { widgetId: SURVEYS.SUBMITTING_EDIT_CARD_REQUEST, throttle: 'session' });
    }
    yield put(resetEditCard());
  } catch (e) {
    yield put({ type: ActionTypes.CARDS.EDIT.FAILURE, payload: e });
    yield put(addToastAlert(Alert.EDIT_CARD_ERROR, 'error'));
    yield put(resetEditCard());
  }
}

function* editMultipleCardDetail(action) {
  const header = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };

  try {
    yield put(addToastAlert(Alert.EDIT_MULTIPLE_CARD_LOADING));
    yield call(fetchClient, {
      url: `cards-api/v1/cards/${action.payload.grandfatherClientNumber}/update`,
      method: 'POST',
      body: action.payload.updateMultipleCardRequest,
      headers: header,
      apiServer: Env.getApimBaseUrl(),
    });
    yield put({
      type: ActionTypes.CARDS.UPDATE_MULTIPLE.SUCCESS,
    });
    yield put(addToastAlert(Alert.EDIT_CARD_SUCCESS, 'success'));

    if (Env.getIsZonkaEnabled() && typeof window._zf === 'function' && SURVEYS.SUBMITTING_EDIT_CARD_REQUEST) {
      window._zf('startForm', { widgetId: SURVEYS.SUBMITTING_EDIT_CARD_REQUEST, throttle: 'session' });
    }
    yield put(resetUpdateMultipleCardDetails());
  } catch (e) {
    yield put({ type: ActionTypes.CARDS.UPDATE_MULTIPLE.FAILURE, payload: e });
    // Feature flag: When VITE_EV_WAY4 is disabled, check for AmpCharge-specific errors
    let errorMessage = Alert.EDIT_MULTIPLE_CARD_ERROR;
    if (!Env.getIsEvWay4Enabled()) {
      const errorResponse = e?.response?.data?.errorMessage || '';
      const isAmpcharge = yield* getIsAmpcharge();
      errorMessage =
        isAmpcharge || isAmpchargeErrorMessage(errorResponse)
          ? Alert.UPDATE_MULTIPLE_CARD_STATUS_ERROR_AMPCHARGE
          : Alert.EDIT_MULTIPLE_CARD_ERROR;
    }
    yield put(addToastAlert(errorMessage, 'error'));
    yield put(resetUpdateMultipleCardDetails());
  }
}

function* cancelPendingTransferBatch(action) {
  const header = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };

  const transferTargets = action.payload.transferTargets;
  const keys = Object.keys(transferTargets);
  const targetIdentifiers = [];

  keys.forEach((key) => {
    const targetIdentifier = {
      targetContractSearchMethod: 'CONTRACT_NUMBER',
      targetContractIdentifier: key,
      cardsList: transferTargets[key].join(';'),
    };

    targetIdentifiers.push(targetIdentifier);
  });

  const payload = {
    targetIdentifiers: targetIdentifiers,
    grandParentAccountNumber: action.payload.grandParentAccountNumber,
  };

  try {
    yield put(addToastAlert(Alert.DELETE_TRANSFER_CARD_LOADING));
    yield call(fetchClient, {
      url: 'cards-api/v1/cards/transfer/pending',
      method: 'DELETE',
      body: payload,
      headers: header,
      apiServer: Env.getApimBaseUrl(),
    });
    yield put(cancelPendingTransferBatchSuccess());
    yield put(addToastAlert(Alert.DELETE_TRANSFER_CARD_SUCCESS, 'success'));
  } catch (e) {
    yield put(cancelPendingTransferBatchFailure());
    yield put(addToastAlert(Alert.DELETE_TRANSFER_CARD_ERROR, 'error'));
  }
}

function* updatePendingTransferBatch(action) {
  const header = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };

  const transferSources = action.payload.transferSources;
  const keys = Object.keys(transferSources);
  const sourceIdentifiers = [];

  keys.forEach((key) => {
    const sourceIdentifier = {
      sourceContractSearchMethod: 'CONTRACT_NUMBER',
      sourceContractIdentifier: key,
      cardsList: transferSources[key].join(';'),
    };

    sourceIdentifiers.push(sourceIdentifier);
  });

  const payload = {
    sourceIdentifiers: sourceIdentifiers,
    targetContractSearchMethod: 'CONTRACT_NUMBER',
    targetContractIdentifier: action.payload.targetContractIdentifier,
    newEffectiveDate: action.payload.newEffectiveDate,
    newProductCode: 'ISSCRDACARD',
    grandParentAccountNumber: action.payload.grandParentAccountNumber,
    targetGrandParentAccountNumber: action.payload.targetGrandParentAccountNumber,
  };

  try {
    yield put(addToastAlert(Alert.EDIT_TRANSFER_CARD_LOADING));
    yield call(fetchClient, {
      url: 'cards-api/v1/cards/transfer/pending',
      method: 'PUT',
      body: payload,
      headers: header,
      apiServer: Env.getApimBaseUrl(),
    });
    yield put(updatePendingTransferBatchSuccess());
    yield put(addToastAlert(Alert.EDIT_TRANSFER_CARD_SUCCESS, 'success'));
  } catch (e) {
    yield put(updatePendingTransferBatchFailure());
    yield put(addToastAlert(Alert.EDIT_TRANSFER_CARD_ERROR, 'error'));
  }
}

function* downloadPendingTransfers(action) {
  let headerPayload = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };

  try {
    yield put(addToastAlert(Alert.DOWNLOAD_TRANSFERS_LOADING));
    const response = yield call(fetchClient, {
      method: 'GET',
      url: 'cards-api/v1/cards/transfer/pending/download',
      apiServer: Env.getApimBaseUrl(),
      headers: headerPayload,
      queryParams: {
        ...action.payload,
      },
      responseType: 'blob',
    });

    if (isMobile()) {
      var reader = new FileReader();
      var blobData = new Blob([response], { type: 'application/vnd.ms-excel' });
      reader.onload = function () {
        window.location.href = reader.result;
      };
      reader.readAsDataURL(blobData);
    } else {
      yield call(fileDownload, response, pendingTransfersDownloadFileName);
    }
    yield put(pendingTransfersDownloadSuccess());
    yield put(addToastAlert(Alert.DOWNLOAD_TRANSFERS_SUCCESS, 'success'));
  } catch {
    yield put(pendingTransfersDownloadFailure());
    yield put(addToastAlert(Alert.DOWNLOAD_TRANSFERS_ERROR, 'error'));
  }
}

function* globalSearch(action) {
  let headerPayload = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };
  const accounts = yield select((state) => state.userStore.loggedInUser.accounts);

  try {
    const response = yield call(fetchClient, {
      method: 'GET',
      url: 'cards-api/v1/cards/search',
      apiServer: Env.getApimBaseUrl(),
      headers: headerPayload,
      queryParams: {
        searchTerm: action.payload.searchTerm,
      },
    });

    if (response?.result && response?.result.length === 0)
      yield put({ type: ActionTypes.CARDS.GLOBAL_SEARCH.SUCCESS, payload: response });

    if (response?.resultType === 'Card' || response?.resultType === 'Registration') {
      yield put(
        setAssignedGrandparentAccount(
          accounts.find((acc) => acc.accountNumber === response.result?.[0]?.selectedAccount)
        )
      );
      action.payload.navigate('/cards', { state: 'globalSearch', replace: true });

      action.payload.navigate('/cards', { replace: true });
    } else if (response?.resultType === 'Cash') {
      yield put(setAssignedGrandparentAccount(accounts.find((acc) => acc.accountNumber === response?.selectedAccount)));
      action.payload.navigate('/cash', { state: 'globalSearch', replace: true });

      yield put(getCashOrdersListSuccess(response));
    }

    yield put({ type: ActionTypes.CARDS.GLOBAL_SEARCH.SUCCESS, payload: response });
  } catch (e) {
    yield put({ type: ActionTypes.CARDS.GLOBAL_SEARCH.FAILURE, payload: e });
  }
}

function getTransferCardErrorMessage(errorMessage) {
  if (!errorMessage || typeof errorMessage !== 'string') {
    return null;
  }

  const classifierMatch = errorMessage.match(/ClassifierName=([^\]]+)/);
  if (classifierMatch?.[1]) {
    const classifierName = classifierMatch[1].trim();
    if (classifierName) {
      return `Failed card transfer, '${classifierName}' do not match. Please contact our Customer Service team on ${Env.getCustomerServicePhone()} for further assistance.`;
    }
  }

  if (/Date Open is too old for imported contract/i.test(errorMessage)) {
    return `Failed card transfer, transfer date is too old for the imported contract. Please contact our Sustomer Service team on ${Env.getCustomerServicePhone()} for further assistance`;
  }

  return null;
}

// Helper functions for AmpCharge card restrictions (used when VITE_EV_WAY4 is disabled)
function* getIsAmpcharge() {
  const cardDetail = yield select((state) => state.cardDetailView.cardDetail);
  return cardDetail?.ampChargeEnabled || false;
}

function isAmpchargeErrorMessage(errorMessage) {
  return errorMessage.includes(' EV');
}

export default function* cardsSaga() {
  yield all([
    takeLatest(ActionTypes.CARDS.REQUEST, getCards),
    takeLeading(ActionTypes.CARDS.DETAIL.REQUEST, getCardDetail),
    takeLatest(ActionTypes.CARDS.DOWNLOAD.REQUEST, getCardsFile),
    takeLatest(ActionTypes.CARDS.UPDATE_PIN.REQUEST, updatePinForCards),
    takeLatest(ActionTypes.CARDS.RESET_PIN.REQUEST, resetPinForCards),
    takeLatest(ActionTypes.CARDS.TRANSFER_SINGLE.REQUEST, transferSingle),
    takeLatest(ActionTypes.CARDS.TRANSFER_BATCH.REQUEST, transferBatch),
    takeLatest(ActionTypes.CARDS.PENDING.REQUEST, getPendingTransferCards),
    takeLatest(ActionTypes.CARDS.ACTIVATE.REQUEST, activateCard),
    takeLatest(ActionTypes.CARDS.ACTIVATE_MULTIPLE.REQUEST, activateMultipleCards),
    takeLatest(ActionTypes.CARDS.CARD_OPTIONS.REQUEST, setCardUsageOptions),
    takeLatest(ActionTypes.CARDS.STATUS.REQUEST, setCardStatus),
    throttle(MEDIUM_THROTTLE, ActionTypes.CARDS.MULTIPLE_CARDS_STATUS.REQUEST, setMultipleCardStatus),
    takeLatest(ActionTypes.CARDS.CREATE.REQUEST, createCard),
    takeLatest(ActionTypes.CARDS.EDIT.REQUEST, editCardDetail),
    takeLatest(ActionTypes.CARDS.UPDATE_MULTIPLE.REQUEST, editMultipleCardDetail),
    throttle(MEDIUM_THROTTLE, ActionTypes.CARDS.ACCOUNT_DEFAULT.REQUEST, getCardAccountDefaults),
    takeLatest(ActionTypes.CARDS.CANCEL_PENDING_TRANSFER_BATCH.REQUEST, cancelPendingTransferBatch),
    takeLatest(ActionTypes.CARDS.UPDATE_PENDING_TRANSFER_BATCH.REQUEST, updatePendingTransferBatch),
    takeLatest(ActionTypes.CARDS.PENDING_TRANSFERS_DOWNLOAD.REQUEST, downloadPendingTransfers),
    takeLatest(ActionTypes.CARDS.GLOBAL_SEARCH.REQUEST, globalSearch),
  ]);
}
